import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { disciplineRatingKeys } from "@/lib/query";

/**
 * Response contract for `GET /api/dashboard/resources/:id/discipline-ratings`.
 * Mirrors the backend contract exactly — do not rename fields locally.
 *
 * One row per HELD Discipline (docs/specs/discipline-rating-fit-score.md Story
 * E2 AC1) — SA:5, DM:2 render as two independent rows, never a blended number.
 * `rating: null` means the Resource holds the Discipline but has no rating yet.
 */
export interface DisciplineRating {
  disciplineId: string;
  disciplineCode: string;
  disciplineName: string;
  rating: number | null;
  confirmed: boolean;
  confirmedAt: string | null;
  confirmedBy: string | null;
  assignedAt: string | null;
  assignedBy: string | null;
  /**
   * The Discipline's fixed 1–5 scale with per-level help text (guidance for what each
   * level means for this Discipline). Powers the rating-capture tooltips. Empty only for
   * a Discipline whose levels have not been seeded yet.
   */
  levels: { level: number; helpText: string | null }[];
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

/**
 * Discipline Ratings for a Resource's held Disciplines. Gated on `enabled` so
 * callers can defer the fetch until the Resource Detail drawer is open, per
 * the "fetch fresh data when opened" dialog rule in .claude/rules/frontend.md.
 */
export function useDisciplineRatings(resourceId: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && resourceId.length > 0;
  return useQuery<DisciplineRating[]>({
    queryKey: disciplineRatingKeys.list(resourceId),
    queryFn: () =>
      jsonFetch<DisciplineRating[]>(`/api/dashboard/resources/${resourceId}/discipline-ratings`),
    enabled,
  });
}

/**
 * RM assigns/modifies one or more Discipline Ratings. Every rating lands (or
 * stays) UNCONFIRMED — no Fit Score recalc fires here (Story W3 AC1/BC1).
 * `[RM]` only — there is no SA self-submit path in this flow (D-0715-E).
 */
export function useAssignDisciplineRatings(resourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ratings: Array<{ disciplineId: string; rating: number }>) =>
      jsonFetch<{ updated: number }>(`/api/dashboard/resources/${resourceId}/discipline-ratings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ratings }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: disciplineRatingKeys.list(resourceId) });
      toast.success("Discipline Rating saved — pending RM confirmation");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/**
 * RM confirms a Resource's currently-assigned Discipline Rating. Triggers a
 * Fit Score recalc on every Solution this Resource fills as a Primary
 * (Story W2 AC1) — server-side, not reflected here beyond invalidation.
 * `[RM]` only.
 */
export function useConfirmDisciplineRating(resourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (disciplineId: string) =>
      jsonFetch<{ confirmed: boolean }>(
        `/api/dashboard/resources/${resourceId}/discipline-ratings/${disciplineId}/confirm`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: disciplineRatingKeys.list(resourceId) });
      toast.success("Discipline Rating confirmed");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
